import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isNull } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { Button, Popover } from '@unite-us/app-components';
import Notifier from 'common/helpers/Notifier';
import { SearchableSelectField } from 'common/form/SearchableSelectField';
import { REQUEST_UNASSIGNED } from 'src/common/utils/Workqueue/hooks/useAssigneeOptions';

const ASSIGN_TO_ME_REGEX = /\s*\(Assign to me\)/gi;

const AssignedUserDisplay = ({ assigneeId, assigneeOptions }) => {
  let assignedUserName = (assigneeOptions.find((opt) => opt.value === assigneeId)?.label);
  assignedUserName = assignedUserName?.replace(ASSIGN_TO_ME_REGEX, '') || '';
  return (
    <>
      <p className="text-text-blue text-xs leading-4 mb-1">Assigned User</p>
      <p className="text-sm text-text-blue font-semibold">{assignedUserName}</p>
    </>
  );
};

AssignedUserDisplay.propTypes = {
  assigneeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  assigneeId: PropTypes.string.isRequired,
};

export const AssignToUserWidget = ({
  assigneeOptions,
  currentAssigneeId,
  onAssignmentChange,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [tempAssigneeId, setTempAssigneeId] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const isUnassigned = isNull(currentAssigneeId);

  const handleAssigneeChange = async (newAssigneeId) => {
    if (isUpdating) return;

    // Optimistic update
    setTempAssigneeId(newAssigneeId);
    setIsUpdating(true);
    setIsOpen(false);

    try {
      await onAssignmentChange(newAssigneeId);
    } catch (error) {
      const errorMessage = error?.message || 'Failed to update assignment';
      setTempAssigneeId(null);
      setIsUpdating(false);
      Notifier.dispatch('error', errorMessage);
    }
  };

  useEffect(() => {
    // wait until current assignee is updated to finish "updating"
    const hasBeenUnassigned = tempAssigneeId === REQUEST_UNASSIGNED;
    if ((isNull(currentAssigneeId) && hasBeenUnassigned) || (currentAssigneeId === tempAssigneeId)) {
      setTempAssigneeId(null);
      setIsUpdating(false);
    }
  }, [currentAssigneeId, tempAssigneeId]);

  const handleEdit = () => {
    setIsOpen((previousValue) => !previousValue);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on scrollbar or during scroll
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, { passive: true });
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
    return undefined;
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <Popover
        className="p-0 border-0"
        content={isOpen && (
          <SearchableSelectField
            className="w-88"
            hideLabel
            immediate
            onChange={({ value: val }) => handleAssigneeChange(val)}
            options={assigneeOptions}
            placeholder="Search"
          />
        )}
        isVisible={isOpen}
        position="bottom"
      >
        <div
          className={`
            w-80 h-16 px-4
            border border-solid border-dark-border-blue rounded
            flex justify-between items-center
          `}
        >
          <div>
            {isUpdating && (
              <>
                <Skeleton height={20} width={200} />
                <Skeleton height={20} width={200} />
              </>
            )}
            {!isUpdating && (
              isUnassigned ? (
                <h4 className="text-text-blue text-base font-semibold mb-1">
                  Assign a User
                </h4>
              ) : (
                <AssignedUserDisplay
                  assigneeId={tempAssigneeId || currentAssigneeId}
                  assigneeOptions={assigneeOptions}
                  isUpdating={isUpdating}
                />
              )
            )}
          </div>
          <Button
            label="Edit"
            onClick={handleEdit}
            link
            size="small"
            disabled={isUpdating}
            icon={{
              color: 'action-blue',
              name: 'Pencil',
              position: 'right',
            }}
          />
        </div>
      </Popover>
    </div>
  );
};

AssignToUserWidget.propTypes = {
  currentAssigneeId: PropTypes.string,
  assigneeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onAssignmentChange: PropTypes.func.isRequired,
};

AssignToUserWidget.defaultProps = {
  currentAssigneeId: null,
};
